import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import { sample_repertorys } from "@screens/sampleData";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import Button from "@components/ui/buttons/Button";
import Dropzone from "@components/ui/blocks/Dropzone";
import { useState, useEffect, useRef, useMemo } from "react";
import TextField from "@components/ui/textfields/TextField";
import { ContactInfos, PartnerInfos, Project } from "types";
import { updateNested, updateNestedArray } from "helpers/updateHelpers";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { MemberAssignmentBlock } from "@components/ui/blocks/MemberAssignmentBlock";
import { CreateContactModal } from "@components/modals/CreateContactModal";
import { showNotification } from "context/notification.service";
import { projectsApi } from "@services/api/projects";
import { invitationsApi } from "@services/api/invitations";
import { usersApi } from "@services/api/users";
import { AddPartnerToProjectModal } from "@components/modals/AddPartnerToProjectModal";
import { useProjectStore } from "@store/projectStore";
import { resolveProjectPartners, stripPartnerSnapshot } from "@utils/resolvePartner";
import { addSectionToCompanyTypes, PROJECT_PARTNER_CATEGORY_OPTIONS } from "@constants/partnerTypes";
import { geocodeAddress } from "@helpers/geocodingHelper";
import { compressImage } from "@utils/compressImage";
import { billingTemplatesApi, BillingTemplate } from "@services/api/billingTemplates";
import { Timestamp } from "firebase/firestore";
import { useDataStore } from "@store/user_org";
import { isDESSINATEUR } from "@helpers/accessHelpers";
import HelpTooltip from "@components/ui/HelpTooltip";

function BillingTab({
  project,
  existingBilling,
  hasProcessedSteps,
  billingTemplates,
  projectTemplates,
  localBillingTemplateId,
  setLocalBillingTemplateId,
  localMontantTotal,
  setLocalMontantTotal,
  localProjectTemplateId,
  setLocalProjectTemplateId,
  billingPctOverrides,
  setBillingPctOverrides,
  billingLabelOverrides,
  setBillingLabelOverrides,
}: {
  project: Project;
  existingBilling: any;
  hasProcessedSteps: boolean;
  billingTemplates: BillingTemplate[];
  projectTemplates: { id: string; name: string }[];
  localBillingTemplateId: string;
  setLocalBillingTemplateId: (v: string) => void;
  localMontantTotal: string;
  setLocalMontantTotal: (v: string) => void;
  localProjectTemplateId: string;
  setLocalProjectTemplateId: (v: string) => void;
  billingPctOverrides: Record<number, string>;
  setBillingPctOverrides: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  billingLabelOverrides: Record<number, string>;
  setBillingLabelOverrides: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}) {
  const bt = billingTemplates.find((b) => b.id === localBillingTemplateId);
  const totalHT = parseFloat(localMontantTotal) || 0;
  const TVA = 0.2;
  const fmt = (v: number) => v.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const ttc = (ht: number) => Math.round(ht * (1 + TVA) * 100) / 100;

  // Merge template steps with existing project steps to determine status
  const steps = useMemo(() => {
    if (!bt) return [];
    return bt.steps.map((templateStep, idx) => {
      const projectStep = existingBilling?.steps?.[idx];
      const isProcessed = projectStep && projectStep.status && projectStep.status !== "pending";
      return {
        ...templateStep,
        status: projectStep?.status || "pending",
        montant: projectStep?.montant || 0,
        isProcessed: !!isProcessed,
        invoice_number: projectStep?.invoice_number,
      };
    });
  }, [bt, existingBilling]);

  // Processed steps have frozen amounts; pending steps share the remaining budget
  const processedTotal = steps.filter((s) => s.isProcessed).reduce((sum, s) => sum + s.montant, 0);
  const remainingHT = totalHT - processedTotal;

  const getPendingStepPct = (idx: number) => {
    return billingPctOverrides[idx] !== undefined ? (parseFloat(billingPctOverrides[idx]) || 0) : steps[idx]?.percentage ?? 0;
  };

  // Percentages are shares of the GRAND total (consistent with how the
  // project view and stored montants work), NOT of the remaining budget.
  // For pending steps: HT = totalHT * pct / 100. For processed (already
  // invoiced) steps: HT = frozen montant. This keeps the modal's Total in
  // agreement with the project page even when some steps are already paid.
  const getStepHT = (idx: number) => {
    const step = steps[idx];
    if (!step) return 0;
    if (step.isProcessed) return step.montant;
    return Math.round(totalHT * getPendingStepPct(idx) / 100 * 100) / 100;
  };

  const demarrageSteps = steps.map((s, i) => ({ ...s, _idx: i })).filter((s) => s.is_demarrage);
  const avancementSteps = steps.map((s, i) => ({ ...s, _idx: i })).filter((s) => !s.is_demarrage);
  // Validation is on ALL steps summing to 100% (processed steps keep their
  // stored percentage; pending steps use the editable override/template %).
  const allPctTotal = steps.reduce((sum, s, i) => sum + (s.isProcessed ? (s.percentage ?? 0) : getPendingStepPct(i)), 0);
  const computedTotal = steps.reduce((sum, _, i) => sum + getStepHT(i), 0);

  const statusLabel = (status: string) => {
    switch (status) {
      case "draft": return "Brouillon";
      case "finalized": return "Finalisée";
      case "sent": return "Envoyée";
      case "paid": return "Payée";
      case "creating": return "Création...";
      case "error": return "Erreur";
      default: return "";
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "paid": return "text-emerald-600 bg-emerald-50";
      case "sent": return "text-blue-600 bg-blue-50";
      case "finalized": return "text-blue-500 bg-blue-50";
      case "draft": return "text-amber-600 bg-amber-50";
      default: return "text-neutral-400 bg-neutral-50";
    }
  };

  return (
    <>
      <div className="text-h3 font-medium text-neutral-500">
        <div className="flex flex-row items-center gap-1.5">
          <span>Modèle de facturation</span>
          <HelpTooltip content="Définit les étapes de facturation et leurs pourcentages par défaut. Changer de modèle réinitialise les pourcentages des étapes non encore émises ; les étapes déjà facturées ne sont pas modifiées." />
        </div>
      </div>
      {hasProcessedSteps && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-h4 text-amber-700 flex items-center gap-2">
          <Icon1 name="warning" size={14} color="orange" />
          Des factures ont déjà été traitées. Le modèle ne peut pas être changé. Seuls le montant total et les étapes en attente sont modifiables.
        </div>
      )}
      <DropDown
        label="Modèle de facturation"
        options={[
          { value: "", label: "Aucun" },
          ...billingTemplates.map((b) => ({ value: b.id, label: b.name })),
        ]}
        value={localBillingTemplateId}
        onSelect={(val) => {
          if (hasProcessedSteps) return;
          setLocalBillingTemplateId(val as string);
          setBillingPctOverrides({});
          setBillingLabelOverrides({});
        }}
        disabled={hasProcessedSteps}
      />
      {(() => {
        // Project-template picker: only meaningful once a billing template
        // is selected AND that template has any mappings. The mapping
        // chosen here decides which controlpoints will trigger each
        // billing step (linked_step_ids). Without a pick, we leave the
        // links empty rather than guess.
        if (!localBillingTemplateId) return null;
        const mappingIds = Object.keys(bt?.mappings || {});
        if (mappingIds.length === 0) return null;
        const options = mappingIds
          .map((id) => {
            const pt = projectTemplates.find((t) => t.id === id);
            return { value: id, label: pt?.name || `Modèle ${id.slice(0, 8)}…` };
          });
        return (
          <>
            <div className="text-h3 font-medium text-neutral-500 mt-2">
              <div className="flex flex-row items-center gap-1.5">
                <span>Modèle de projet pour lier les étapes</span>
                <HelpTooltip content="Indique quel modèle de projet a servi de base à ce projet, pour que les étapes de facturation se déclenchent automatiquement à la validation des bons points de contrôle. Laissez vide si vous préférez lier manuellement." />
              </div>
            </div>
            <DropDown
              label="Modèle de projet"
              options={[{ value: "", label: "Aucun (liens manuels)" }, ...options]}
              value={localProjectTemplateId}
              onSelect={(val) => {
                if (hasProcessedSteps) return;
                setLocalProjectTemplateId(val as string);
              }}
              disabled={hasProcessedSteps}
            />
          </>
        );
      })()}
      {localBillingTemplateId && (
        <>
          <TextField
            label="Honoraires HT (€)"
            value={localMontantTotal}
            // Accept the French decimal comma: translate "," → "." (and drop
            // spaces) so the full amount is parsed, not truncated at the comma.
            onChange={(v) => setLocalMontantTotal(v.replace(/,/g, ".").replace(/\s/g, ""))}
            placeholder="150000"
          />
          {hasProcessedSteps && totalHT > 0 && (
            <div className="text-h5 text-neutral-400">
              Montant déjà facturé : {fmt(processedTotal)} € HT — Restant à répartir : {fmt(remainingHT)} € HT
            </div>
          )}
          {bt && totalHT > 0 && (
            <div className="flex flex-col mt-2">
              {/* Column headers */}
              <div className="flex flex-row items-center px-2 py-1 text-h5 text-neutral-400">
                <span className="flex-1" />
                <span className="w-16 text-right">%</span>
                <span className="w-24 text-right">HT</span>
                <span className="w-24 text-right">TTC</span>
              </div>

              {/* Démarrage group */}
              {demarrageSteps.length > 0 && (
                <>
                  <div className="flex flex-row items-center px-2 py-1.5 rounded-lg bg-green-50 mt-1">
                    <span className="text-h3 font-medium text-green-700 flex-1">Démarrage</span>
                  </div>
                  {demarrageSteps.map((step) => {
                    const stepHT = getStepHT(step._idx);
                    return (
                      <div key={`d-${step._idx}`} className={`flex flex-row items-center px-2 pl-6 py-0.5 ${step.isProcessed ? "opacity-70" : ""}`}>
                        {step.isProcessed ? (
                          <span className="text-h4 text-neutral-500 flex-1 flex items-center gap-2">
                            {step.label}
                            <span className={`text-h5 px-1.5 py-0.5 rounded ${statusColor(step.status)}`}>
                              {statusLabel(step.status)}
                            </span>
                            {step.invoice_number && <span className="text-h5 text-neutral-400">#{step.invoice_number}</span>}
                          </span>
                        ) : (
                          <input
                            type="text"
                            className="text-h4 text-neutral-500 flex-1 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                            value={billingLabelOverrides[step._idx] !== undefined ? billingLabelOverrides[step._idx] : step.label}
                            onChange={(e) => setBillingLabelOverrides((prev) => ({ ...prev, [step._idx]: e.target.value }))}
                          />
                        )}
                        <span className="w-16 text-right text-h5 text-neutral-400 flex items-center justify-end gap-0.5">
                          {step.isProcessed ? (
                            <span className="text-neutral-300">—</span>
                          ) : (
                            <>
                              <input
                                type="number"
                                className="w-12 text-right text-h5 text-neutral-500 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                                value={billingPctOverrides[step._idx] !== undefined ? billingPctOverrides[step._idx] : step.percentage}
                                onChange={(e) => setBillingPctOverrides((prev) => ({ ...prev, [step._idx]: e.target.value }))}
                              />%
                            </>
                          )}
                        </span>
                        <span className="w-24 text-right text-h4 text-neutral-500">{fmt(stepHT)} €</span>
                        <span className="w-24 text-right text-h5 text-neutral-400">{fmt(ttc(stepHT))} €</span>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Avancement steps */}
              {avancementSteps.length > 0 && (
                <>
                  <div className="flex flex-row items-center px-2 py-1.5 mt-2">
                    <span className="text-h4 text-neutral-400 flex-1">
                      Avancement{hasProcessedSteps && remainingHT > 0 ? ` — ${fmt(remainingHT)} € HT restant` : ""}
                    </span>
                  </div>
                  {avancementSteps.map((step) => {
                    const stepHT = getStepHT(step._idx);
                    return (
                      <div key={`a-${step._idx}`} className={`flex flex-row items-center px-2 py-1 rounded-lg ${step.isProcessed ? "bg-neutral-100 opacity-70" : "bg-neutral-50"}`}>
                        {step.isProcessed ? (
                          <span className="text-h3 font-medium flex-1 flex items-center gap-2">
                            {step.label}
                            <span className={`text-h5 px-1.5 py-0.5 rounded ${statusColor(step.status)}`}>
                              {statusLabel(step.status)}
                            </span>
                            {step.invoice_number && <span className="text-h5 text-neutral-400">#{step.invoice_number}</span>}
                          </span>
                        ) : (
                          <input
                            type="text"
                            className="text-h3 font-medium flex-1 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                            value={billingLabelOverrides[step._idx] !== undefined ? billingLabelOverrides[step._idx] : step.label}
                            onChange={(e) => setBillingLabelOverrides((prev) => ({ ...prev, [step._idx]: e.target.value }))}
                          />
                        )}
                        <span className="w-16 text-right text-h4 text-neutral-500 flex items-center justify-end gap-0.5">
                          {step.isProcessed ? (
                            <span className="text-neutral-300">—</span>
                          ) : (
                            <>
                              <input
                                type="number"
                                className="w-12 text-right text-h4 text-neutral-500 border border-neutral-200 rounded px-1 py-0.5 focus:outline-none focus:border-blue-400"
                                value={billingPctOverrides[step._idx] !== undefined ? billingPctOverrides[step._idx] : step.percentage}
                                onChange={(e) => setBillingPctOverrides((prev) => ({ ...prev, [step._idx]: e.target.value }))}
                              />%
                            </>
                          )}
                        </span>
                        <span className="w-24 text-right text-h3 font-medium text-neutral-700">{fmt(stepHT)} €</span>
                        <span className="w-24 text-right text-h4 text-neutral-500">{fmt(ttc(stepHT))} €</span>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Total row */}
              <div className="flex flex-row items-center px-2 py-1.5 border-t mt-2">
                <span className="text-h3 font-semibold flex-1">Total</span>
                <span className="w-16" />
                <span className="w-24 text-right text-h3 font-semibold text-neutral-800">{fmt(computedTotal)} €</span>
                <span className="w-24 text-right text-h3 font-semibold text-neutral-800">{fmt(ttc(computedTotal))} €</span>
              </div>
              {Math.abs(allPctTotal - 100) > 0.01 && (
                <div className="text-h5 text-red-500 mt-1 px-2">
                  Le total des pourcentages doit être égal à 100% (actuellement {Math.round(allPctTotal * 100) / 100}%)
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

/** Collect every controlpoint id from a project/template themes tree. */
const collectControlpointIds = (proj: any): Set<string> => {
  const ids = new Set<string>();
  for (const theme of proj?.themes || []) {
    for (const step of theme?.steps || []) {
      for (const cp of step?.controlpoints || []) {
        if (cp?.id) ids.add(cp.id);
      }
    }
  }
  return ids;
};

/**
 * Detect the project template a project was seeded from, by controlpoint-set
 * containment. Returns the id of the unique smallest project template whose
 * controlpoints fully include the project's controlpoints — a high-confidence
 * match. Returns undefined when there is no full-containment match or when the
 * smallest match is ambiguous (a tie), so callers stay on the safe path and
 * leave billing links empty rather than guessing wrong.
 */
const detectSourceTemplateId = (
  templates: any[],
  projectCpIds: Set<string>,
): string | undefined => {
  if (projectCpIds.size === 0) return undefined;
  const containing: { id: string; size: number }[] = [];
  for (const t of templates || []) {
    if (!t?.id) continue;
    const tIds = collectControlpointIds(t);
    let contains = true;
    projectCpIds.forEach((id) => {
      if (!tIds.has(id)) contains = false;
    });
    if (contains) containing.push({ id: t.id, size: tIds.size });
  }
  if (containing.length === 0) return undefined;
  containing.sort((a, b) => a.size - b.size);
  // Ambiguous if two templates tie for the smallest size — don't guess.
  if (containing.length > 1 && containing[0].size === containing[1].size) {
    return undefined;
  }
  return containing[0].id;
};

export const ModifyProjectModal = ({
  onClose,
  project,
  setProject,
}: {
  onClose: () => void;
  project?: any;
  setProject?: any;
}) => {
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [selectedRepertory, setSelectedRepertory] = useState<string>(
    sample_repertorys[0],
  );
  const [localProject, setLocalProject] = useState<Project>(project);
  const [electronicSignature, setElectronicSignature] =
    useState<boolean>(false);
  const [internalDocument, setInternalDocument] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("Général");
  const [showCreateContact, setShowCreateContact] = useState<boolean>(false);
  const [addPartnerForSection, setAddPartnerForSection] = useState<string | false>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [confirmReset, setConfirmReset] = useState<boolean>(false);
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const updateProject = useProjectStore((state) => state.updateProject);
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const storePartners = useProjectStore((state) => state.partners);
  const hydratedPartners = resolveProjectPartners(localProject, storePartners);

  // Photo tab state
  const [coverPhoto, setCoverPhoto] = useState<File | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(
    project?.infos?.general?.photo_file?.url || null,
  );
  const [removePhoto, setRemovePhoto] = useState(false);
  const coverPhotoInputRef = useRef<HTMLInputElement>(null);

  // Contact email lookup state (same as CreateProjectModal)
  type UserSuggestion = {
    id: string;
    display_name: string;
    email: string;
    photo_url?: string;
  };
  const [contactSuggestions, setContactSuggestions] = useState<
    Record<number, UserSuggestion | null>
  >({});
  const lookupTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>(
    {},
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleEmailChange = (index: number, value: string) => {
    const wasLinked = !!localProject.infos.client.contacts[index]?.user_id;
    // Keep the linked account on edit. Changing the email of an already-linked
    // contact just changes its notification address by default; the account
    // (and the client's access) only changes if the org explicitly chooses to
    // at save time. Unlinked contacts still get the "link existing account"
    // suggestion below.
    setLocalProject((prev) =>
      updateNestedArray(prev, "infos.client.contacts", index, { email: value }),
    );
    if (wasLinked) return;
    setContactSuggestions((prev) => ({ ...prev, [index]: null }));
    if (lookupTimers.current[index]) clearTimeout(lookupTimers.current[index]);
    if (!value.includes("@") || !value.includes(".")) return;
    lookupTimers.current[index] = setTimeout(async () => {
      const result = await usersApi.lookup(value);
      setContactSuggestions((prev) => ({ ...prev, [index]: result }));
    }, 400);
  };

  const handleLinkContact = (index: number) => {
    const suggestion = contactSuggestions[index];
    if (!suggestion) return;
    const contact = localProject.infos.client.contacts[index];
    const nameParts = suggestion.display_name.split(" ");
    setLocalProject(
      updateNestedArray(localProject, "infos.client.contacts", index, {
        user_id: suggestion.id,
        first_name: contact.first_name || nameParts[0] || "",
        last_name: contact.last_name || nameParts.slice(1).join(" ") || "",
      }),
    );
    setContactSuggestions((prev) => ({ ...prev, [index]: null }));
  };

  const handleCoverPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showNotification({ type: "error", message: "La photo ne doit pas dépasser 5 Mo" });
      return;
    }
    setCoverPhoto(file);
    setCoverPhotoPreview(URL.createObjectURL(file));
    setRemovePhoto(false);
  };

  // Billing tab state
  const [billingTemplates, setBillingTemplates] = useState<BillingTemplate[]>([]);
  const [localBillingTemplateId, setLocalBillingTemplateId] = useState<string>(
    project?.billing?.billing_template_id || "",
  );
  const [localMontantTotal, setLocalMontantTotal] = useState<string>(
    project?.billing?.montant_total?.toString() || "",
  );
  // Which project template's mapping to use for tying billing steps to
  // controlpoints. Defaults to whatever's already on the project; user can
  // pick another one (e.g. for legacy projects with no template stored).
  const [localProjectTemplateId, setLocalProjectTemplateId] = useState<string>(
    (project as any)?.project_template_id || "",
  );
  const [billingPctOverrides, setBillingPctOverrides] = useState<Record<number, string>>({});
  const [billingLabelOverrides, setBillingLabelOverrides] = useState<Record<number, string>>({});

  const existingBilling = project?.billing;
  const hasProcessedSteps = existingBilling?.steps?.some(
    (s: any) => s.status && s.status !== "pending",
  ) ?? false;

  // Track whether user modified billing fields
  const billingChanged = localBillingTemplateId !== (project?.billing?.billing_template_id || "") ||
    localMontantTotal !== (project?.billing?.montant_total?.toString() || "") ||
    Object.keys(billingPctOverrides).length > 0 ||
    Object.keys(billingLabelOverrides).length > 0;

  // Only validate billing percentages if user actually changed billing
  const billingPctValid = useMemo(() => {
    if (!billingChanged) return true; // didn't touch billing = valid
    if (!localBillingTemplateId) return true; // no billing = valid
    const bt = billingTemplates.find((b) => b.id === localBillingTemplateId);
    if (!bt) return true;
    const totalHT = parseFloat(localMontantTotal) || 0;
    if (!totalHT) return true; // no amount yet = don't block
    // Percentages are shares of the grand total, so ALL steps (already-
    // invoiced + pending) must sum to 100%. Processed steps keep their stored
    // percentage; pending steps use the editable override (or template) %.
    const allTotal = bt.steps.reduce((sum, step, idx) => {
      const existing = existingBilling?.steps?.[idx];
      const isProcessed = existing && existing.status && existing.status !== "pending";
      const pct = isProcessed
        ? step.percentage
        : (billingPctOverrides[idx] !== undefined ? (parseFloat(billingPctOverrides[idx]) || 0) : step.percentage);
      return sum + pct;
    }, 0);
    return Math.abs(allTotal - 100) <= 0.01;
  }, [billingChanged, localBillingTemplateId, billingTemplates, localMontantTotal, billingPctOverrides, existingBilling]);

  useEffect(() => {
    if (!userData?.org_id) return;
    billingTemplatesApi.list(userData.org_id).then(setBillingTemplates).catch(() => {});
  }, [userData?.org_id]);

  // Reuse store-loaded project templates; map to the {id, name} shape the
  // BillingTab dropdown expects.
  const projectTemplateOptions = useMemo(
    () => projectTemplates.map((t: any) => ({
      id: t.id,
      name: t.infos?.general?.name || t.id,
    })),
    [projectTemplates],
  );

  async function handleSave() {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await doSave();
    } finally {
      setIsSaving(false);
    }
  }

  async function doSave() {
    // Changing the email of a contact already linked to an account transfers
    // that account's access to the new email. We detect those changes here and
    // run the transfer (relink new account + revoke old + notify both sides)
    // at the end, after the rest of the project save. The old user_id is kept
    // on the contact for now so it stays in guest_user_ids until the transfer
    // reassigns it server-side.
    const originalEmailByUserId = new Map<string, string>(
      (project?.infos?.client?.contacts || [])
        .filter((c: ContactInfos) => !!c.user_id)
        .map((c: ContactInfos) => [c.user_id!, c.email || ""]),
    );
    const emailTransfers = (localProject.infos.client.contacts || [])
      .map((c: ContactInfos, index: number) => ({ c, index }))
      .filter(
        ({ c }) =>
          !!c.user_id &&
          originalEmailByUserId.has(c.user_id) &&
          (c.email || "").trim().toLowerCase() !==
            (originalEmailByUserId.get(c.user_id) || "").trim().toLowerCase(),
      )
      .map(({ c, index }) => ({ index, newEmail: (c.email || "").trim() }));

    // Email-only contacts: any new contact with email and no user_id yet
    const originalContactEmails = new Set<string>(
      (project?.infos?.client?.contacts || [])
        .map((c: ContactInfos) => c.email)
        .filter(Boolean),
    );
    const emailOnlyContacts = (localProject.infos.client.contacts || []).filter(
      (c: ContactInfos) =>
        !c.user_id && c.email && !originalContactEmails.has(c.email),
    );

    // Look up or invite email-only contacts to get their user_ids
    const contactsToInvite: ContactInfos[] = [];
    for (const contact of emailOnlyContacts) {
      const existingUser = await usersApi.lookup(contact.email!);
      if (existingUser) {
        contact.user_id = existingUser.id;
      } else {
        contactsToInvite.push(contact);
      }
    }

    // Send invitations for brand-new contacts BEFORE computing guest_user_ids
    // so their freshly assigned user_ids are included in the authoritative list.
    if (contactsToInvite.length > 0) {
      const invitationResults = await Promise.allSettled(
        contactsToInvite.map((contact: ContactInfos) =>
          invitationsApi.create({
            invitee_email: contact.email!,
            invitee_name:
              `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
              undefined,
            invitation_type: "web",
            context: {
              access_type: "GUEST",
              project_id: localProject.id,
            },
          }),
        ),
      );
      invitationResults.forEach((result, i) => {
        if (result.status === "fulfilled" && result.value.user_id) {
          contactsToInvite[i].user_id = result.value.user_id;
        }
      });
    }

    // guest_user_ids = the AUTHORITATIVE current list of user_ids, derived
    // strictly from the current contacts. Contacts removed on the frontend
    // are correctly dropped from the guest list (the backend uses SET, not
    // ArrayUnion).
    const authoritativeGuestUserIds = (localProject.infos.client.contacts || [])
      .filter((c: ContactInfos) => !!c.user_id)
      .map((c: ContactInfos) => c.user_id!);

    await projectsApi.update(localProject.id, {
      guest_user_ids: authoritativeGuestUserIds,
    });

    // Persist updated contacts (with newly assigned user_ids from the
    // lookup/invitation step above) back to the project's infos.
    if (emailOnlyContacts.some((c: ContactInfos) => c.user_id)) {
      await projectsApi.update(localProject.id, {
        infos: { ...localProject.infos, client: { contacts: localProject.infos.client.contacts } },
      });
    }

    // Geocode if address fields changed
    const addressChanged =
      localProject.infos.general.address !== project?.infos?.general?.address ||
      localProject.infos.general.city !== project?.infos?.general?.city ||
      localProject.infos.general.postal !== project?.infos?.general?.postal;

    let updatedLocalProject = localProject;
    if (addressChanged) {
      const coordinates = await geocodeAddress(
        localProject.infos.general.address,
        localProject.infos.general.city,
        localProject.infos.general.postal,
      );
      if (coordinates) {
        await projectsApi.update(localProject.id, { coordinates });
        updatedLocalProject = {
          ...localProject,
          infos: {
            ...localProject.infos,
            general: { ...localProject.infos.general, coordinates },
          },
        };
      }
    }

    // Photo: upload new or remove existing
    if (coverPhoto) {
      try {
        const compressedCover = await compressImage(coverPhoto);
        const [uploaded] = await projectsApi.uploadFiles(localProject.id, [compressedCover]);
        const photoFile = { id: uploaded.id, url: uploaded.url, name: uploaded.name, octets: uploaded.size, created_at: Timestamp.now() };
        await projectsApi.update(localProject.id, { photo_file: photoFile });
        updatedLocalProject = {
          ...updatedLocalProject,
          infos: {
            ...updatedLocalProject.infos,
            general: { ...updatedLocalProject.infos.general, photo_file: photoFile },
          },
        };
      } catch (e) {
        console.error("Failed to upload cover photo:", e);
      }
    } else if (removePhoto && project?.infos?.general?.photo_file) {
      await projectsApi.update(localProject.id, { photo_file: null });
      updatedLocalProject = {
        ...updatedLocalProject,
        infos: {
          ...updatedLocalProject.infos,
          general: { ...updatedLocalProject.infos.general, photo_file: undefined },
        },
      };
    }

    // Billing: build updated billing data (only if user changed billing fields)
    if (billingChanged && localBillingTemplateId) {
      const bt = billingTemplates.find((b) => b.id === localBillingTemplateId);
      if (bt) {
        const totalHT = parseFloat(localMontantTotal) || 0;
        // Processed (already-invoiced) steps keep their frozen montant.
        // Pending steps are a share of the GRAND total — montant = totalHT *
        // pct / 100 — so the stored montants stay consistent with the project
        // view and the modal Total. (Percentages sum to 100% across all steps.)

        // Resolve which controlpoints each step should be tied to. The
        // billing template stores `mappings[project_template_id]` — only
        // safe to apply if THIS project was created from that template
        // (controlpoint IDs are reused across templates, so a mapping
        // designed for a different template may "match" structurally
        // while linking the wrong steps semantically).
        //
        // Prefer an explicit id: the template picked in this modal, or one
        // already stored on the project. For legacy projects that never had
        // `project_template_id` persisted (it was historically dropped at
        // creation), fall back to detecting the source template by
        // controlpoint-set CONTAINMENT — the unique smallest template whose
        // controlpoints fully include this project's. That is a
        // high-confidence match (not a loose structural overlap), so it is
        // safe to auto-link; an ambiguous or partial match returns undefined
        // and we leave the links empty rather than guessing wrong.
        const projectCpIds = collectControlpointIds(localProject);
        const mappings = bt.mappings || {};
        const effectiveTemplateId: string | undefined =
          localProjectTemplateId
          || (project as any)?.project_template_id
          || detectSourceTemplateId(projectTemplates, projectCpIds);
        const chosenMapping = effectiveTemplateId ? mappings[effectiveTemplateId] : undefined;

        const newSteps = bt.steps.map((templateStep, idx) => {
          const existingStep = existingBilling?.steps?.[idx];
          const isProcessed = existingStep && existingStep.status && existingStep.status !== "pending";
          if (isProcessed) {
            return { ...existingStep };
          }
          const pct = billingPctOverrides[idx] !== undefined ? (parseFloat(billingPctOverrides[idx]) || 0) : templateStep.percentage;
          const label = billingLabelOverrides[idx] !== undefined ? billingLabelOverrides[idx] : templateStep.label;
          // Prefer manual links the user already set; otherwise pull from
          // the resolved mapping, filtered to controlpoints that actually
          // exist in this project so we never write dangling references.
          let linked = existingStep?.linked_step_ids || [];
          if (linked.length === 0 && chosenMapping) {
            const sm = chosenMapping.find((m) => m.step_index === idx);
            linked = (sm?.linked_steps || []).filter((id) => projectCpIds.has(id));
          }
          return {
            ...(existingStep || {}),
            label,
            abbreviation: templateStep.abbreviation || undefined,
            percentage: pct,
            montant: Math.round(totalHT * pct / 100 * 100) / 100,
            is_demarrage: templateStep.is_demarrage,
            status: "pending",
            linked_step_ids: linked,
            payment_delay_days: templateStep.payment_delay_days,
            project_delay_days: templateStep.project_delay_days,
            step_gap_delay_days: templateStep.step_gap_delay_days,
          };
        });

        const billingUpdate = {
          billing_template_id: localBillingTemplateId,
          montant_total: totalHT,
          steps: newSteps,
          pennylane_customer_id: existingBilling?.pennylane_customer_id,
        };
        const updatePayload: any = { billing: billingUpdate };
        // Persist the resolved project template (explicit selection OR the
        // detected source template) so future modifications resolve the same
        // mapping without re-detecting. Only write when it actually differs
        // from what's stored, to avoid a no-op field churn.
        if (effectiveTemplateId
            && effectiveTemplateId !== (project as any)?.project_template_id) {
          updatePayload.project_template_id = effectiveTemplateId;
        }
        await projectsApi.update(localProject.id, updatePayload);
        updatedLocalProject = {
          ...updatedLocalProject,
          billing: billingUpdate as any,
          ...(effectiveTemplateId
            ? { project_template_id: effectiveTemplateId } as any
            : {}),
        };
      } else {
        // billingTemplates not loaded yet — surface instead of silent no-op
        showNotification({
          type: "error",
          message: "Modèles de facturation non chargés. Veuillez réessayer.",
        });
        return;
      }
    } else if (billingChanged && existingBilling?.billing_template_id && !localBillingTemplateId) {
      // Removed billing template
      await projectsApi.update(localProject.id, { billing: null });
      updatedLocalProject = { ...updatedLocalProject, billing: undefined as any };
    }

    // Transfer access for any linked contact whose email was changed. Persist
    // the current infos first so the server reconciles against the latest
    // contact data, then run each transfer (relink + revoke + notify both
    // sides). Each call returns the fully reconciled project.
    if (emailTransfers.length > 0) {
      await projectsApi.update(localProject.id, { infos: localProject.infos });
      for (const t of emailTransfers) {
        try {
          updatedLocalProject = await projectsApi.transferContactEmail(
            localProject.id,
            t.index,
            t.newEmail,
          );
        } catch (e) {
          console.error("Failed to transfer contact email:", e);
          showNotification({
            type: "error",
            message: "Erreur lors du transfert de l'email d'un contact",
          });
        }
      }
    }

    if (setProject) {
      setProject(updatedLocalProject);
    }
    updateProject(updatedLocalProject);
    showNotification({
      type: "success",
      message: "Projet modifié avec succès",
    });
    onClose();
  }

  return (
    <BaseModal
      title="Modifier un projet"
      startIcon={<Icon1 name="modify" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <ChunkyTabs
          short_tabs={(() => {
            const all = [
              "Général",
              "Adresses",
              "Equipe",
              "Artisans",
              "Photo",
              "Facturation",
              "Étapes",
            ];
            // Dessinateurs are read-only on construction lifecycle data,
            // so they don't get the Artisans / Facturation / Étapes tabs.
            if (isDESSINATEUR(userData)) {
              return all.filter(
                (t) => t !== "Artisans" && t !== "Facturation" && t !== "Étapes",
              );
            }
            return all;
          })()}
          setActiveTab={(tab) => setActiveTab(tab)}
          activeTab={activeTab}
        ></ChunkyTabs>
        {activeTab === "Général" && (
          <>
            <TextField
              label="Numéro"
              placeholder="A001"
              value={localProject.infos.general.num}
              onChange={(value) =>
                setLocalProject(
                  updateNested(localProject, "infos.general.num", value),
                )
              }
            />
            <div className="text-h3 font-medium text-neutral-500 mt-2">Contact principal</div>
            <DropDown
              label="Civilité"
              options={[
                { value: "M.", label: "M." },
                { value: "Mme", label: "Mme" },
                { value: "SCI", label: "SCI" },
              ]}
              value={localProject.infos.client.contacts[0]?.prefix || "M."}
              onSelect={(value) => {
                const contacts = [...localProject.infos.client.contacts];
                const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                contacts[0] = { ...existing, prefix: value as ContactInfos["prefix"], sci_name: value === "SCI" ? existing.sci_name : undefined };
                const name = value === "SCI" ? (existing.sci_name || "") : existing.last_name;
                setLocalProject({
                  ...localProject,
                  infos: {
                    ...localProject.infos,
                    client: { ...localProject.infos.client, contacts },
                    general: { ...localProject.infos.general, name },
                  },
                });
              }}
            />
            {localProject.infos.client.contacts[0]?.prefix === "SCI" && (
              <TextField
                label="Nom de la SCI"
                placeholder="Nom de la SCI"
                value={localProject.infos.client.contacts[0]?.sci_name || ""}
                onChange={(value) => {
                  const contacts = [...localProject.infos.client.contacts];
                  contacts[0] = { ...contacts[0], sci_name: value };
                  setLocalProject({
                    ...localProject,
                    infos: {
                      ...localProject.infos,
                      client: { ...localProject.infos.client, contacts },
                      general: { ...localProject.infos.general, name: value },
                    },
                  });
                }}
              />
            )}
            <div className="flex flex-row gap-2">
              <TextField
                label="Nom"
                placeholder="DUPONT"
                value={localProject.infos.client.contacts[0]?.last_name || ""}
                onChange={(value) => {
                  const contacts = [...localProject.infos.client.contacts];
                  const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                  contacts[0] = { ...existing, last_name: value };
                  const isSCI = existing.prefix === "SCI";
                  setLocalProject({
                    ...localProject,
                    infos: {
                      ...localProject.infos,
                      client: { ...localProject.infos.client, contacts },
                      general: { ...localProject.infos.general, ...(!isSCI && { name: value }) },
                    },
                  });
                }}
              />
              <TextField
                label="Prénom"
                placeholder="Jean"
                value={localProject.infos.client.contacts[0]?.first_name || ""}
                onChange={(value) => {
                  const contacts = [...localProject.infos.client.contacts];
                  const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                  contacts[0] = { ...existing, first_name: value };
                  setLocalProject({
                    ...localProject,
                    infos: { ...localProject.infos, client: { ...localProject.infos.client, contacts } },
                  });
                }}
              />
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex-1 min-w-0">
                <TextField
                  label="Téléphone"
                  placeholder="06 01 02 03 04"
                  type="phone"
                  value={localProject.infos.client.contacts[0]?.phone_number || ""}
                  onChange={(value) => {
                    const contacts = [...localProject.infos.client.contacts];
                    const existing = contacts[0] || { first_name: "", last_name: "", prefix: "M." as const };
                    contacts[0] = { ...existing, phone_number: value };
                    setLocalProject({
                      ...localProject,
                      infos: { ...localProject.infos, client: { ...localProject.infos.client, contacts } },
                    });
                  }}
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <TextField
                  label="Email"
                  placeholder="marie.curie@life.com"
                  value={localProject.infos.client.contacts[0]?.email || ""}
                  onChange={(value) => handleEmailChange(0, value)}
                />
                {localProject.infos.client.contacts[0]?.user_id && (
                  <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium px-1">
                    <span>✓</span>
                    <span>Compte existant lié</span>
                  </div>
                )}
                {contactSuggestions[0] && !localProject.infos.client.contacts[0]?.user_id && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium text-blue-700 shrink-0">
                      {contactSuggestions[0]!.display_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-800 truncate">
                        {contactSuggestions[0]!.display_name}
                      </div>
                      <div className="text-xs text-neutral-400">
                        Compte existant
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleLinkContact(0)}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    >
                      Lier ce compte
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setContactSuggestions((prev) => ({
                          ...prev,
                          [0]: null,
                        }))
                      }
                      className="text-neutral-400 hover:text-neutral-600 text-sm leading-none"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
            {/* Additional contacts (index 1+) */}
            {localProject.infos.client.contacts.slice(1).map(
              (contact: ContactInfos, idx: number) => {
                const index = idx + 1;
                return (
                  <div
                    key={index}
                    className="flex flex-col gap-2 mb-4 p-4 rounded-xl border"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-h3 font-medium text-neutral-500">
                        Contact {index + 1}
                      </span>
                      <SecondaryButton
                        danger={true}
                        variant="small"
                        onClick={() =>
                          setLocalProject({
                            ...localProject,
                            infos: {
                              ...localProject.infos,
                              client: {
                                ...localProject.infos.client,
                                contacts:
                                  localProject.infos.client.contacts.filter(
                                    (_, i) => i !== index,
                                  ),
                              },
                            },
                          })
                        }
                      >
                        Supprimer
                      </SecondaryButton>
                    </div>
                    <DropDown
                      label="Civilité"
                      options={[
                        { value: "M.", label: "M." },
                        { value: "Mme", label: "Mme" },
                        { value: "SCI", label: "SCI" },
                      ]}
                      value={contact.prefix || "M."}
                      onSelect={(value) =>
                        setLocalProject(
                          updateNestedArray(
                            localProject,
                            "infos.client.contacts",
                            index,
                            {
                              prefix: value as ContactInfos["prefix"],
                              sci_name: value === "SCI" ? contact.sci_name : undefined,
                            },
                          ),
                        )
                      }
                    />
                    {contact.prefix === "SCI" && (
                      <TextField
                        label="Nom de la SCI"
                        placeholder="Nom de la SCI"
                        value={contact.sci_name || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { sci_name: value },
                            ),
                          )
                        }
                      />
                    )}
                    <div className="flex flex-row gap-2">
                      <TextField
                        label="Nom"
                        placeholder="Dupont"
                        value={contact.last_name || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { last_name: value },
                            ),
                          )
                        }
                      />
                      <TextField
                        label="Prénom"
                        placeholder="Jean"
                        value={contact.first_name || ""}
                        onChange={(value) =>
                          setLocalProject(
                            updateNestedArray(
                              localProject,
                              "infos.client.contacts",
                              index,
                              { first_name: value },
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="flex flex-row gap-2">
                      <div className="flex-1 min-w-0">
                        <TextField
                          label="Téléphone"
                          placeholder="06 01 02 03 04"
                          type="phone"
                          value={contact.phone_number || ""}
                          onChange={(value) =>
                            setLocalProject(
                              updateNestedArray(
                                localProject,
                                "infos.client.contacts",
                                index,
                                { phone_number: value },
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <TextField
                          label="Email"
                          placeholder="marie.curie@life.com"
                          value={contact.email || ""}
                          onChange={(value) => handleEmailChange(index, value)}
                        />
                      </div>
                    </div>
                    {contact.user_id && (
                      <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium px-1">
                        <span>✓</span>
                        <span>Compte existant lié</span>
                      </div>
                    )}
                    {contactSuggestions[index] && !contact.user_id && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                        <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium text-blue-700 shrink-0">
                          {contactSuggestions[index]!.display_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-neutral-800 truncate">
                            {contactSuggestions[index]!.display_name}
                          </div>
                          <div className="text-xs text-neutral-400">
                            Compte existant
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleLinkContact(index)}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
                        >
                          Lier ce compte
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setContactSuggestions((prev) => ({
                              ...prev,
                              [index]: null,
                            }))
                          }
                          className="text-neutral-400 hover:text-neutral-600 text-sm leading-none"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                );
              },
            )}
            <SecondaryButton
              startIcon={<Icon1 name="plus" size={18}></Icon1>}
              label="Ajouter un contact"
              variant="large"
              onClick={() => setShowCreateContact(true)}
            />
            {showCreateContact && (
              <CreateContactModal
                onClose={() => setShowCreateContact(false)}
                onSave={async (newContact) => {
                  // Add to local state first so the UI updates instantly.
                  const nextContacts = [
                    ...localProject.infos.client.contacts,
                    newContact,
                  ];
                  const nextProject = {
                    ...localProject,
                    infos: {
                      ...localProject.infos,
                      client: {
                        ...localProject.infos.client,
                        contacts: nextContacts,
                      },
                    },
                  };
                  setLocalProject(nextProject);

                  // Persist immediately — the "Enregistrer" in the sub-modal
                  // implies the contact is saved; without this step the
                  // contact is only in local state and is lost if the user
                  // cancels the outer modal.
                  try {
                    if (newContact.email && !newContact.user_id) {
                      try {
                        const existingUser = await usersApi.lookup(newContact.email);
                        if (existingUser) {
                          newContact.user_id = existingUser.id;
                        } else {
                          const inv = await invitationsApi.create({
                            invitee_email: newContact.email,
                            invitee_name:
                              `${newContact.first_name || ""} ${newContact.last_name || ""}`.trim() ||
                              undefined,
                            invitation_type: "web",
                            context: {
                              access_type: "GUEST",
                              project_id: localProject.id,
                            },
                          });
                          if (inv.user_id) newContact.user_id = inv.user_id;
                        }
                      } catch (err) {
                        console.error("Contact lookup/invite failed:", err);
                      }
                    }

                    const persistedContacts = [
                      ...localProject.infos.client.contacts,
                      newContact,
                    ];
                    const guestUserIds = persistedContacts
                      .filter((c) => !!c.user_id)
                      .map((c) => c.user_id!);

                    await projectsApi.update(localProject.id, {
                      guest_user_ids: guestUserIds,
                      infos: {
                        ...localProject.infos,
                        client: { contacts: persistedContacts },
                      },
                    });

                    // Re-sync local state with the persisted contact (user_id
                    // may have been filled in above).
                    setLocalProject((prev) => ({
                      ...prev,
                      infos: {
                        ...prev.infos,
                        client: {
                          ...prev.infos.client,
                          contacts: persistedContacts,
                        },
                      },
                    }));
                    showNotification({ type: "success", message: "Contact ajouté" });
                  } catch (err) {
                    console.error("Failed to persist new contact:", err);
                    showNotification({
                      type: "error",
                      message: "Erreur lors de l'ajout du contact",
                    });
                  }
                }}
              />
            )}
            <AreaTextField
              label="Notes"
              placeholder="Notes sur le projet"
              value={localProject?.infos.general.notes || ""}
              onChange={(value) =>
                setLocalProject(
                  updateNested(localProject, "infos.general.notes", value),
                )
              }
            />
            <div className="text-h5 text-neutral-300 italic -mt-1">
              visible uniquement en interne
            </div>
          </>
        )}
        {activeTab === "Adresses" && (
          <>
            <div className="text-h3 font-medium text-neutral-500">Domicile</div>
            <TextField
              label="Adresse"
              placeholder="1 rue des fleurs"
              value={localProject.infos.general.home_address || ""}
              onChange={(value) =>
                setLocalProject(updateNested(localProject, "infos.general.home_address", value))
              }
            />
            <div className="flex flex-row gap-2">
              <TextField
                label="Ville"
                placeholder="Rennes"
                value={localProject.infos.general.home_city || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.home_city", value))
                }
              />
              <TextField
                label="Code postal"
                placeholder="35000"
                value={localProject.infos.general.home_postal || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.home_postal", value))
                }
              />
            </div>

            <div className="text-h3 font-medium text-neutral-500 mt-4">Adresse du projet</div>
            <TextField
              label="Adresse"
              placeholder="1 rue des fleurs"
              value={localProject.infos.general.address || ""}
              onChange={(value) =>
                setLocalProject(updateNested(localProject, "infos.general.address", value))
              }
            />
            <div className="flex flex-row gap-2">
              <TextField
                label="Ville"
                placeholder="Rennes"
                value={localProject.infos.general.city || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.city", value))
                }
              />
              <TextField
                label="Code postal"
                placeholder="35000"
                value={localProject.infos.general.postal || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.postal", value))
                }
              />
            </div>
            <div className="flex flex-row gap-2">
              <TextField
                label="Lot"
                placeholder="Lot 12"
                value={localProject.infos.general.lot || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.lot", value))
                }
              />
              <TextField
                label="Section"
                placeholder="AB"
                value={localProject.infos.general.section || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.section", value))
                }
              />
              <TextField
                label="N° cadastre"
                placeholder="1234"
                value={localProject.infos.general.cadastre_number || ""}
                onChange={(value) =>
                  setLocalProject(updateNested(localProject, "infos.general.cadastre_number", value))
                }
              />
            </div>
          </>
        )}
        {activeTab === "Equipe" && (
          <>
            <MemberAssignmentBlock
              team={localProject.infos.team}
              onTeamChange={(team) =>
                setLocalProject({
                  ...localProject,
                  infos: { ...localProject.infos, team },
                })
              }
            />
          </>
        )}
        {activeTab === "Artisans" && (
          <>
            {addPartnerForSection !== false && (
              <AddPartnerToProjectModal
                onClose={() => setAddPartnerForSection(false)}
                filterByType={addPartnerForSection || undefined}
                categoryOptions={PROJECT_PARTNER_CATEGORY_OPTIONS}
                onSelect={(partner: PartnerInfos) => {
                  // Global "Ajouter un intervenant" sets addPartnerForSection
                  // to "" — the modal's category picker writes the chosen
                  // section into partner.company_types, so fall back to that.
                  const sectionKey = addPartnerForSection || partner.company_types?.[0];
                  setLocalProject((prev) => {
                    const existing = prev.infos.partners.find((p) => p.id === partner.id);
                    if (existing) {
                      if (sectionKey) {
                        const merged = addSectionToCompanyTypes(existing.company_types, sectionKey);
                        if (merged.length !== (existing.company_types?.length || 0)) {
                          const updated = prev.infos.partners.map((p) =>
                            p.id === partner.id ? { ...p, company_types: merged } : p,
                          );
                          return { ...prev, infos: { ...prev.infos, partners: updated } };
                        }
                      }
                      return prev;
                    }
                    const stripped = stripPartnerSnapshot(partner);
                    const partnerWithSection = sectionKey
                      ? { ...stripped, company_types: addSectionToCompanyTypes(stripped.company_types, sectionKey) }
                      : stripped;
                    return {
                      ...prev,
                      infos: {
                        ...prev.infos,
                        partners: [...prev.infos.partners, partnerWithSection],
                      },
                    };
                  });
                  setAddPartnerForSection(false);
                }}
                existingPartnerIds={addPartnerForSection
                  ? hydratedPartners
                      .filter((p) => p.company_types?.includes(addPartnerForSection))
                      .map((p) => p.id)
                  : []}
              />
            )}
            <div className="flex flex-col gap-1">
              {PROJECT_PARTNER_CATEGORY_OPTIONS.map((section) => {
                const candidates = hydratedPartners
                  .filter((p) => p.company_types?.includes(section.value))
                  .sort((a, b) => (a.company_types?.length || 0) - (b.company_types?.length || 0));
                if (candidates.length === 0) {
                  return (
                    <div
                      key={section.value}
                      className="flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors bg-neutral-50 border-neutral-200 cursor-pointer hover:bg-neutral-100"
                      onClick={() => setAddPartnerForSection(section.value)}
                    >
                      <div className="flex flex-row items-center gap-2 flex-1">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 bg-neutral-300`} />
                        <div className="flex flex-col">
                          <div className="text-h3 font-medium text-neutral-400">
                            {section.label.toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <Icon1 name="plus" size={14} color="grey" />
                    </div>
                  );
                }
                return candidates.map((assigned) => (
                  <div
                    key={`${section.value}-${assigned.id}`}
                    className="flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors bg-white border-neutral-200"
                  >
                    <div className="flex flex-row items-center gap-2 flex-1">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-neutral-800" />
                      <div className="flex flex-col">
                        <div className="text-h3 font-medium text-black">
                          {section.label.toUpperCase()}
                        </div>
                        <div className="text-h4 text-neutral-400">
                          {assigned.company_name || assigned.display_name}
                        </div>
                      </div>
                    </div>
                    <SecondaryButton
                      danger={true}
                      variant="small"
                      onClick={() => {
                        const updated = localProject.infos.partners
                          .map((p) => {
                            if (p.id !== assigned.id) return p;
                            const remaining = (p.company_types || []).filter(
                              (t) => t !== section.value,
                            );
                            return remaining.length > 0
                              ? { ...p, company_types: remaining }
                              : null;
                          })
                          .filter(Boolean) as typeof localProject.infos.partners;
                        setLocalProject({
                          ...localProject,
                          infos: {
                            ...localProject.infos,
                            partners: updated,
                          },
                        });
                      }}
                    >
                      Retirer
                    </SecondaryButton>
                  </div>
                ));
              })}
            </div>
            {/* Partners whose type is not one of the known categories */}
            {hydratedPartners
              .filter(
                (p) => !PROJECT_PARTNER_CATEGORY_OPTIONS.some((s) => p.company_types?.includes(s.value)),
              )
              .map((partner) => (
                <div
                  key={partner.id}
                  className="flex flex-row items-center justify-between px-4 py-3 rounded-lg border"
                >
                  <div className="flex flex-col">
                    <div className="text-h2 text-black font-medium">
                      {partner.company_name || partner.display_name}
                    </div>
                    <div className="text-h3 text-neutral-400">
                      {partner.company_name && partner.display_name
                        ? partner.display_name
                        : ""}
                    </div>
                  </div>
                  <SecondaryButton
                    danger={true}
                    variant="small"
                    onClick={() => {
                      setLocalProject({
                        ...localProject,
                        infos: {
                          ...localProject.infos,
                          partners: localProject.infos.partners.filter(
                            (p) => p.id !== partner.id,
                          ),
                        },
                      });
                    }}
                  >
                    Retirer
                  </SecondaryButton>
                </div>
              ))}
            <SecondaryButton
              startIcon={<Icon1 name="plus" size={16} />}
              label="Ajouter un intervenant"
              variant="large"
              onClick={() => setAddPartnerForSection("")}
            />
          </>
        )}
        {activeTab === "Étapes" && (
          <div className="flex flex-col gap-4">
            <div className="text-h3 text-neutral-500">
              Réinitialiser les thèmes, étapes et points de contrôle à partir
              d'un modèle de projet.
            </div>
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-orange-600 font-medium text-h3">
                <Icon1 name="warning" size={16} color="orange" />
                Cette action remplacera tous les thèmes, étapes et points de
                contrôle existants du projet.
              </div>
              <DropDown
                label="Modèle de projet"
                value={selectedTemplateId}
                options={[
                  { value: "", label: "— Choisir un modèle —" },
                  ...projectTemplates.map((t) => ({
                    value: t.id,
                    label: t.infos.general.name,
                  })),
                ]}
                onSelect={(v) => {
                  setSelectedTemplateId(v as string);
                  setConfirmReset(false);
                }}
              />
              {selectedTemplateId && !confirmReset && (
                <SecondaryButton
                  danger
                  variant="small"
                  onClick={() => setConfirmReset(true)}
                >
                  Réinitialiser depuis ce modèle
                </SecondaryButton>
              )}
              {selectedTemplateId && confirmReset && (
                <div className="flex flex-col gap-2">
                  <div className="text-h3 text-orange-700 font-medium">
                    Confirmer la réinitialisation ?
                  </div>
                  <div className="flex gap-2">
                    <Button
                      label="Confirmer"
                      onClick={() => {
                        const template = projectTemplates.find(
                          (t) => t.id === selectedTemplateId,
                        );
                        if (!template) return;
                        setLocalProject({
                          ...localProject,
                          themes: structuredClone(template.themes),
                        });
                        setConfirmReset(false);
                        setSelectedTemplateId("");
                        showNotification({
                          type: "success",
                          message:
                            "Étapes réinitialisées — cliquez sur Enregistrer pour sauvegarder.",
                        });
                      }}
                    />
                    <SecondaryButton
                      variant="small"
                      onClick={() => setConfirmReset(false)}
                    >
                      Annuler
                    </SecondaryButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === "Photo" && (
          <>
            <div className="text-h3 font-medium text-neutral-500">
              Photo de façade
            </div>
            <div className="text-h5 text-neutral-400">
              Cette photo sera utilisée comme couverture du projet.
            </div>
            {coverPhotoPreview && !removePhoto ? (
              <div className="flex flex-col gap-2">
                <img
                  src={coverPhotoPreview}
                  alt="Aperçu"
                  className="w-full max-h-64 object-cover rounded-lg border"
                />
                <SecondaryButton
                  startIcon={<Icon1 name="delete" size={18} />}
                  label="Supprimer la photo"
                  variant="large"
                  onClick={() => {
                    setCoverPhoto(null);
                    setCoverPhotoPreview(null);
                    setRemovePhoto(true);
                    if (coverPhotoInputRef.current) coverPhotoInputRef.current.value = "";
                  }}
                />
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-neutral-200 rounded-xl p-10 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-neutral-400 transition-colors"
                onClick={() => coverPhotoInputRef.current?.click()}
              >
                <Icon1 name="join" size={24} color="grey" />
                <span className="text-h3 text-neutral-400">
                  Cliquez pour ajouter une photo
                </span>
                <span className="text-h5 text-neutral-300">
                  JPG, PNG, WEBP — 5MB max
                </span>
              </div>
            )}
            <input
              ref={coverPhotoInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleCoverPhotoChange}
              className="hidden"
            />
          </>
        )}
        {activeTab === "Facturation" && (
          <BillingTab
            project={localProject}
            existingBilling={existingBilling}
            hasProcessedSteps={hasProcessedSteps}
            billingTemplates={billingTemplates}
            projectTemplates={projectTemplateOptions}
            localBillingTemplateId={localBillingTemplateId}
            setLocalBillingTemplateId={setLocalBillingTemplateId}
            localMontantTotal={localMontantTotal}
            setLocalMontantTotal={setLocalMontantTotal}
            localProjectTemplateId={localProjectTemplateId}
            setLocalProjectTemplateId={setLocalProjectTemplateId}
            billingPctOverrides={billingPctOverrides}
            setBillingPctOverrides={setBillingPctOverrides}
            billingLabelOverrides={billingLabelOverrides}
            setBillingLabelOverrides={setBillingLabelOverrides}
          />
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label="Enregistrer"
          onClick={() => handleSave()}
          loading={isSaving}
          disabled={!billingPctValid}
        ></Button>
      </div>
    </BaseModal>
  );
};
